/**
 * ラボ 2 の盤面（docs/curriculum/labs.md ラボ 2、components.md §3.2 QueueLab）。
 * - head 方式: 通った人を消さず、`head` を 1 つ進める。箱は動かない。
 * - 詰める方式: `removeFirst()`。残りの全員が 1 歩ずつ前へ動く。
 */
import type { ApplyResult, LabModel } from './types';

export type QueueMode = 'head' | 'shift';

export interface QueueState {
  readonly mode: QueueMode;
  /** 配列全体。head 方式では head より前が通った人。 */
  readonly items: readonly number[];
  /** 次に通る人の添字。詰める方式では常に 0。 */
  readonly head: number;
}

export type QueueOp = { type: 'enqueue'; value: number } | { type: 'dequeue' } | { type: 'count' };

export const QUEUE_LABEL = 'queue';

export function emptyQueue(mode: QueueMode): QueueState {
  return { mode, items: [], head: 0 };
}

/** まだ通っていない人。 */
export function waiting(state: QueueState): readonly number[] {
  return state.items.slice(state.head);
}

function apply(state: QueueState, op: QueueOp): ApplyResult<QueueState> {
  const { mode, items, head } = state;
  switch (op.type) {
    case 'enqueue':
      return {
        state: { mode, items: [...items, op.value], head },
        entry: { code: `queue.append(${op.value})`, tag: '追加', changed: [QUEUE_LABEL] },
        moved: 1,
        message: `${op.value} が列の後ろに付きました。`,
      };
    case 'dequeue': {
      const front = items[head];
      if (front === undefined) {
        return {
          state,
          entry: null,
          moved: 0,
          message:
            mode === 'head'
              ? 'まだ通っていない人がいません。queue[head] は配列の範囲外です。'
              : '列に誰もいません。空の配列の removeFirst() は実行時エラーになります。',
        };
      }
      if (mode === 'head') {
        const s = String(front);
        return {
          state: { mode, items, head: head + 1 },
          entry: {
            code: 'print(queue[head]); head += 1',
            result: s,
            stdout: s,
            tag: '通過',
            changed: [QUEUE_LABEL],
          },
          moved: 0,
          message: `${front} が通りました。head は ${head + 1} です。箱は動いていません。`,
        };
      }
      const rest = items.slice(1);
      return {
        state: { mode, items: rest, head: 0 },
        entry: {
          code: 'queue.removeFirst()',
          result: String(front),
          tag: '通過',
          changed: [QUEUE_LABEL],
        },
        moved: rest.length,
        message: `${front} が通りました。残りの ${rest.length} 人が 1 歩ずつ前へ動きました。`,
      };
    }
    case 'count': {
      const n = items.length - head;
      const s = String(n);
      return {
        state,
        entry: {
          code: mode === 'head' ? 'print(queue.count - head)' : 'print(queue.count)',
          result: s,
          stdout: s,
          tag: '確認',
          changed: [],
        },
        moved: 0,
        message: `まだ通っていないのは ${n} 人です。`,
      };
    }
  }
}

export const queueModel: LabModel<QueueState, QueueOp> = {
  id: 'queue',
  apply,
  declarations: (initial) =>
    initial.mode === 'head' ? ['var queue: [Int] = []', 'var head = 0'] : ['var queue: [Int] = []'],
  labels: () => [QUEUE_LABEL],
  vizLine: (state) =>
    state.mode === 'head' ? 'viz.queue("queue", queue, head: head)' : 'viz.queue("queue", queue)',
  snapshot: (state) => ({ kind: 'queue', value: [...state.items], head: state.head }),
};

/** 通すたびの手間。head 方式は添字を 1 つ進めるだけ（1）、詰める方式は残りの人数。 */
export function dequeueCost(mode: QueueMode, remainingAfter: number): number {
  return mode === 'head' ? 1 : remainingAfter;
}

/** n 人並ばせて全員通したときの手間の合計（labs.md ラボ 2 手順 2: 100 人で 100 と 4,950）。 */
export function totalDequeueCost(mode: QueueMode, n: number): number {
  if (n <= 0) return 0;
  return mode === 'head' ? n : (n * (n - 1)) / 2;
}
